import { useEffect, useState } from 'react';
import useScreenSize from './useScreenSize';

type UseIsFixedProps = {
  containerRef?: React.RefObject<HTMLDivElement>;
  isHomePage?: boolean;
  isSmall?: boolean;
  isAdPage?: boolean;
  headerId?: string;
};

export const useStickyHeader = ({
  containerRef,
  isHomePage = false,
  headerId = 'popup_header',
  isAdPage = false
}: UseIsFixedProps) => {
  const { isSmall } = useScreenSize();

  const [isFixed, setIsFixed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [showFloatButton, setShowFloatButton] = useState(false);
  const [initialPosition, setInitialPosition] = useState(0);
  const [isScrollLocked, setIsScrollLocked] = useState(false);

  const FIX_ON = 10;
  const FIX_OFF = 5;

  useEffect(() => {
    if (containerRef?.current) {
      const position = containerRef?.current?.getBoundingClientRect();
      setInitialPosition(position?.top || 0);
    }
  }, [containerRef?.current, isSmall]);

  useEffect(() => {
    if (containerRef?.current && !isFixed) {
      setHeaderHeight(containerRef.current?.clientHeight || 0);
    }
  }, [containerRef?.current?.clientHeight, isFixed, isSmall]);

  useEffect(() => {
    if (!isSmall) return;

    const handleFocus = () => setIsScrollLocked(true);
    const handleBlur = () => setIsScrollLocked(false);

    window.addEventListener('focusin', handleFocus);
    window.addEventListener('focusout', handleBlur);

    return () => {
      window.removeEventListener('focusin', handleFocus);
      window.removeEventListener('focusout', handleBlur);
    };
  }, [isSmall]);

  useEffect(() => {
    const scrollHandler = () => {
      if (isHomePage && !isSmall) return;

      const banner = document.getElementById('js-lotriver-desk-top-banner');
      const bannerHeight = banner?.offsetHeight || 0;

      const scrollY = window.pageYOffset;
      const isSearchResultPage = !isHomePage && !isAdPage;
      const threshold =
        headerHeight + (isSearchResultPage && isSmall ? 20 : 100);

      const fixOn = isAdPage
        ? initialPosition + bannerHeight
        : threshold + FIX_ON;
      const fixOff = isAdPage
        ? scrollY <= initialPosition + bannerHeight
        : scrollY < threshold - FIX_OFF;

      const isScrollBlocked =
        getComputedStyle(document.body).overflow === 'hidden' ||
        getComputedStyle(document.documentElement).overflow === 'hidden';

      const isFixedHeaderWithOpenedModal =
        isFixed && !scrollY && isScrollBlocked;

      if (
        (!isFixed && scrollY > fixOn && !isScrollLocked) ||
        isFixedHeaderWithOpenedModal
      ) {
        setIsFixed(true);
      } else if (isFixed && fixOff) setIsFixed(false);

      setShowFloatButton(scrollY > 300);
    };

    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [
    headerHeight,
    isFixed,
    isHomePage,
    isSmall,
    isScrollLocked,
    initialPosition
  ]);

  useEffect(() => {
    const topHeader = headerId ? document.getElementById(headerId) : null;
    if (!topHeader) return;

    if (isFixed) {
      topHeader.style.opacity = '0';
      topHeader.style.pointerEvents = 'none';
    } else {
      topHeader.style.opacity = '1';
      topHeader.style.pointerEvents = '';
    }
  }, [isFixed, headerId, isSmall]);

  return { isFixed, headerHeight, showFloatButton };
};
